const SmsOptIn = lazy(() => import("./pages/SmsOptIn"));
const EmployerJobs = lazy(() => import("./pages/EmployerJobs"));
import { useEffect, Component, lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, useSearchParams, Navigate } from "react-router-dom";
import { RouteSeo } from "./lib/seo";
import { Breadcrumbs } from "./components/Breadcrumbs";
import { PageFaqs } from "./components/PageFaqs";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { DeferredWidgets } from "./components/DeferredWidgets";
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from "./components/ui/sonner";
import { DragScroll } from "./hooks/useDragScroll";

import Home from "./pages/Home";
import NotFound from "./pages/NotFound";
import { ALIASES } from "./content/aliases";
const HomeCompact = lazy(() => import("./pages/HomeCompact"));
const Destinations = lazy(() => import("./pages/Destinations"));
const DestinationDetail = lazy(() => import("./pages/DestinationDetail"));
const DestinationRoute = lazy(() => import("./pages/DestinationRoute"));
const HiDestination = lazy(() => import("./pages/HiDestination"));
const HiVisaCheck = lazy(() => import("./pages/HiVisaCheck"));
const VisaRoutes = lazy(() => import("./pages/VisaRoutes"));
const VisaRoutesPreview = lazy(() => import("./pages/VisaRoutesPreview"));
const RadioPage = lazy(() => import("./pages/RadioPage"));
const VisaRouteDetail = lazy(() => import("./pages/VisaRouteDetail"));
const VisaCheck = lazy(() => import("./pages/VisaCheck"));
const VisaCheckHome = lazy(() => import("./pages/VisaCheck").then((m) => ({ default: m.VisaCheckHome })));
const Eligibility = lazy(() => import("./pages/Eligibility"));
const Landing = lazy(() => import("./pages/Landing"));
const Pricing = lazy(() => import("./pages/Pricing"));
const BusinessPlatform = lazy(() => import("./pages/BusinessPlatform"));
const BusinessServices = lazy(() => import("./pages/BusinessServices"));
const Partners = lazy(() => import("./pages/Partners"));
const Consultants = lazy(() => import("./pages/Consultants"));
const Business = lazy(() => import("./pages/Business"));
const Guides = lazy(() => import("./pages/Guides"));
const Team = lazy(() => import("./pages/Team"));
const Author = lazy(() => import("./pages/Team").then((m) => ({ default: m.Author })));
const GuideArticle = lazy(() => import("./pages/GuideArticle"));
const CountryGuide = lazy(() => import("./pages/CountryGuide"));
const Compare = lazy(() => import("./pages/Compare"));
const Refusals = lazy(() => import("./pages/Refusals"));
const Passport = lazy(() => import("./pages/Passport"));
const ProcessingTimes = lazy(() => import("./pages/ProcessingTimes"));
const VisaIndexPage = lazy(() => import("./pages/VisaIndexPage"));
const Appointments = lazy(() => import("./pages/Appointments"));
const BizGuide = lazy(() => import("./pages/BizGuide"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/Blog").then((m) => ({ default: m.BlogPost })));
const Digest = lazy(() => import("./pages/Blog").then((m) => ({ default: m.Digest })));
const Unsubscribe = lazy(() => import("./pages/Blog").then((m) => ({ default: m.Unsubscribe })));
import { SiteBanner } from "./components/SiteBanner";
const Faq = lazy(() => import("./pages/Faq"));
const Guarantee = lazy(() => import("./pages/Guarantee"));
const BusinessHome = lazy(() => import("./pages/BusinessHome"));
const Workspace = lazy(() => import("./pages/Workspace"));
const Account = lazy(() => import("./pages/Account"));
const Portal = lazy(() => import("./pages/Portal"));
const Cases = lazy(() => import("./pages/Cases"));
const Groups = lazy(() => import("./pages/Groups"));
const Join = lazy(() => import("./pages/Join"));
const PartnerPortal = lazy(() => import("./pages/partner/PartnerPortal"));
const StartLink = lazy(() => import("./pages/StartLink"));
const Revenue = lazy(() => import("./pages/Revenue"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Legal = lazy(() => import("./pages/Legal"));
const SharedPlan = lazy(() => import("./pages/SharedPlan"));
const Scorecard = lazy(() => import("./pages/Scorecard"));
const ScorecardEmbed = lazy(() => import("./pages/Scorecard").then((m) => ({ default: m.ScorecardEmbed })));
const PartnerProfile = lazy(() => import("./pages/PartnerProfile"));
const Leads = lazy(() => import("./pages/Leads"));
import { AuthProvider, AuthCallback, useAuth } from "./lib/auth";
import { PortalShell, inShellPath, isAgencyUser } from "./pages/partner/PortalShell";
import { DestinationsProvider, useDestinations } from "./lib/destinations";
import { LocaleProvider, useLocale } from "./lib/locale";
const Funds = lazy(() => import("./pages/Funds"));
const Jobs = lazy(() => import("./pages/Jobs"));
const Compliance = lazy(() => import("./pages/Compliance"));
const CaseTypes = lazy(() => import("./pages/CaseTypes"));
const CaseType = lazy(() => import("./pages/CaseType"));
const Vims = lazy(() => import("./pages/Vims"));
const Contact = lazy(() => import("./pages/Contact"));
const Apply = lazy(() => import("./pages/Apply"));
const Permits = lazy(() => import("./pages/Permits"));
const Auth = lazy(() => import("./pages/Auth"));
const OpsApp = lazy(() => import("./pages/ops/OpsApp"));
const PayThanks = lazy(() => import("./pages/PayThanks"));
const Invite = lazy(() => import("./pages/Invite"));
const About = lazy(() => import("./pages/Company").then((m) => ({ default: m.About })));
const Careers = lazy(() => import("./pages/Company").then((m) => ({ default: m.Careers })));
const Press = lazy(() => import("./pages/Company").then((m) => ({ default: m.Press })));
const HowWeVerify = lazy(() => import("./pages/Trust").then((m) => ({ default: m.HowWeVerify })));
const Sources = lazy(() => import("./pages/Trust").then((m) => ({ default: m.Sources })));
const ToolsIndex = lazy(() => import("./pages/Tools").then((m) => ({ default: m.ToolsIndex })));
const PassportValidity = lazy(() => import("./pages/Tools").then((m) => ({ default: m.PassportValidity })));
const VisaPhoto = lazy(() => import("./pages/Tools").then((m) => ({ default: m.VisaPhoto })));
const TravelChecklist = lazy(() => import("./pages/Tools").then((m) => ({ default: m.TravelChecklist })));
const Glossary = lazy(() => import("./pages/Tools").then((m) => ({ default: m.Glossary })));
import { RadioProvider } from "./lib/radio";
import { useTracking } from "./lib/track";
import { LANDING_SLUGS } from "./content/landing";

// Public marketing URLs are lowercase with no trailing slash; variants redirect so there is one indexable URL per page.
const CASE_SAFE = /^\/(destinations|guides|compare|refusals|visas|tools|consultants|cases|for|blog|processing-times|visa-index|pricing|partners|eligibility|faq|about|contact|jobs|permits|compliance|visa-check|digest|team|guarantee|careers|press|terms|privacy|hi|uk-visa-from-india|canada-visitor-visa|australia-tourist-visa|us-tourist-visa-from-india|schengen-visa-from-india|uae-tourist-visa-from-india|singapore-tourist-visa|japan-tourist-visa|visa-eligibility-check|visa-application-assistance)(\/|$)/i;
function UrlNormalizer() {
  const { pathname, search, hash } = useLocation();
  let next = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  if (CASE_SAFE.test(next)) next = next.toLowerCase();
  if (next === pathname) return null;
  return <Navigate to={`${next || "/"}${search}${hash}`} replace />;
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const go = () => { const el = document.getElementById(hash.slice(1)); if (el) el.scrollIntoView({ block: "start" }); };
      go(); const t = setTimeout(go, 400); return () => clearTimeout(t);
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

class ErrorBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen grid place-items-center p-8 text-center" data-testid="app-error">
        <div>
          <p className="font-display text-3xl text-primary">Something went wrong loading this page.</p>
          <p className="mt-3 text-sm text-muted-foreground">{String(this.state.error?.message || this.state.error)}</p>
          <button onClick={() => window.location.assign("/")} className="mt-6 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground">Back to home</button>
        </div>
      </div>
    );
  }
}

function Layout({ children }) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  useTracking();
  if (pathname.startsWith("/embed/") || pathname.startsWith("/marketing") || pathname.startsWith("/ops")) return <main>{children}</main>;
  // Signed-in agencies work inside their own portal chrome (PortalHeader), not the public business-site navbar.
  if (pathname.startsWith("/business/portal")) return <div className="App"><main>{children}</main><DeferredWidgets cookieOnly /></div>;
  if (inShellPath(pathname) && (isAgencyUser(user) || user === null)) return <div className="App"><PortalShell>{children}</PortalShell><DeferredWidgets cookieOnly /></div>;
  return (
    <div className="App flex min-h-screen flex-col">
      <SiteBanner />
      <Navbar />
      <Breadcrumbs />
      <main className="flex-1">{children}<PageFaqs /></main>
      <Footer />
      <DeferredWidgets />
    </div>
  );
}

// The marketing console now lives inside the Ops back-office; old bookmarks follow.
function LegacyMarketingRedirect() {
  const { pathname, search } = useLocation();
  return <Navigate to={`/ops/marketing${pathname.replace(/^\/marketing/, "")}${search}`} replace />;
}


function PricingRoute() {
  const [params] = useSearchParams();
  return params.get("for") === "business" ? <Navigate to="/business/pricing" replace /> : <Pricing />;
}

function RouteFallback() {
  return <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8" aria-busy="true" data-testid="route-loading"><div className="skel h-8 w-64" /><div className="skel mt-3 h-4 w-96" /><div className="mt-10 grid gap-4 sm:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="skel h-56 rounded-2xl" />)}</div></div>;
}

function AppRoutes() {
  const location = useLocation();
  const { version } = useDestinations();
  const { switches } = useLocale();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
        <>
        <RouteSeo />
        <Suspense fallback={<RouteFallback />}>
        <Routes key={`${version}-${switches || 0}`}>
          <Route path="/" element={<Home />} />
          <Route path="/preview/home" element={<HomeCompact />} />
          <Route path="/preview/visas" element={<VisaRoutesPreview />} />
          <Route path="/radio" element={<RadioPage />} />
          <Route path="/radio/:id" element={<RadioPage />} />
          <Route path="/destinations" element={<Destinations />} />
          <Route path="/destinations/:slug" element={<DestinationDetail />} />
          <Route path="/destinations/:slug/:route" element={<DestinationRoute />} />
          <Route path="/hi/destinations/:slug" element={<HiDestination />} />
          <Route path="/hi/visa-check/india/:slug" element={<HiVisaCheck />} />
          <Route path="/preview/visa/:slug" element={<DestinationDetail clean />} />
          <Route path="/visas" element={<VisaRoutes />} />
          <Route path="/visas/:slug" element={<VisaRouteDetail />} />
          <Route path="/visa-check" element={<VisaCheckHome />} />
          <Route path="/visa-check/:passport" element={<VisaCheck />} />
          <Route path="/visa-check/:passport/:slug" element={<VisaCheck />} />
          <Route path="/eligibility" element={<Eligibility />} />
          <Route path="/pricing" element={<PricingRoute />} />
          <Route path="/business" element={<BusinessHome />} />
          <Route path="/business/pricing" element={<Pricing business />} />
          <Route path="/business/platform" element={<BusinessPlatform />} />
          <Route path="/business/services" element={<BusinessServices />} />
          <Route path="/business/workspace" element={<Workspace />} />
          <Route path="/business/cases" element={<Cases />} />
          <Route path="/business/groups" element={<Groups />} />
          <Route path="/business/groups/:id" element={<Groups />} />
          <Route path="/business/revenue" element={<Revenue />} />
          <Route path="/join/:code" element={<Join />} />
          <Route path="/business/portal" element={<PartnerPortal />} />
          <Route path="/start/:token" element={<StartLink />} />
          <Route path="/business/profile" element={<PartnerProfile />} />
          <Route path="/business/jobs" element={<EmployerJobs />} />
          <Route path="/business/leads" element={<Leads />} />
          <Route path="/business/funds" element={<Funds />} />
          <Route path="/account" element={<Account />} />
          <Route path="/reset" element={<ResetPassword />} />
          <Route path="/privacy" element={<Legal />} />
          <Route path="/terms" element={<Legal />} />
          <Route path="/portal" element={<Portal />} />
          <Route path="/portal/:id" element={<Portal />} />
          <Route path="/portal/:id/:tab" element={<Portal />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/consultants/:slug" element={<Consultants />} />
          <Route path="/consultants" element={<Navigate to="/consultants/visa" replace />} />
          {LANDING_SLUGS.map((s) => <Route key={s} path={`/${s}`} element={<Landing slug={s} />} />)}
          <Route path="/partners/:id/scorecard" element={<Scorecard />} />
          <Route path="/plan/:id" element={<SharedPlan />} />
          <Route path="/for/:slug" element={<Business />} />
          <Route path="/guides" element={<Guides />} />
          <Route path="/guides/read/:slug" element={<GuideArticle />} />
          <Route path="/guides/:slug" element={<CountryGuide />} />
          <Route path="/compare" element={<Compare />} />
          <Route path="/passport/:slug" element={<Passport />} />
          <Route path="/processing-times" element={<ProcessingTimes />} />
          <Route path="/visa-index" element={<VisaIndexPage />} />
          <Route path="/visa-index/:month" element={<VisaIndexPage />} />
          <Route path="/appointments" element={<Appointments />} />
          <Route path="/business/guides/read/:slug" element={<GuideArticle business />} />
          <Route path="/business/guides/:slug" element={<BizGuide />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/digest" element={<Digest />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/marketing/*" element={<LegacyMarketingRedirect />} />
          <Route path="/ops/*" element={<OpsApp />} />
          <Route path="/pay/thanks" element={<PayThanks />} />
          <Route path="/invite/:token" element={<Invite />} />
          <Route path="/about" element={<About />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/press" element={<Press />} />
          <Route path="/how-we-verify" element={<HowWeVerify />} />
          <Route path="/sources" element={<Sources />} />
          <Route path="/tools" element={<ToolsIndex />} />
          <Route path="/sms-opt-in" element={<SmsOptIn />} />
          <Route path="/tools/passport-validity" element={<PassportValidity />} />
          <Route path="/tools/visa-photo" element={<VisaPhoto />} />
          <Route path="/tools/travel-checklist" element={<TravelChecklist />} />
          <Route path="/tools/glossary" element={<Glossary />} />
          <Route path="/compare/:cat/:pair" element={<Compare />} />
          <Route path="/refusals" element={<Refusals />} />
          <Route path="/refusals/:slug" element={<Refusals />} />
          <Route path="/business/guides" element={<Guides business />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/guarantee" element={<Guarantee />} />
          <Route path="/jobs" element={<Jobs />} />
          <Route path="/compliance" element={<Compliance />} />
          <Route path="/cases" element={<CaseTypes />} />
          <Route path="/business/vims" element={<Vims />} />
          <Route path="/cases/:slug" element={<CaseType />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/apply/:slug" element={<Apply />} />
          <Route path="/permits" element={<Permits />} />
          <Route path="/permits/:slug" element={<Permits />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/register" element={<Auth />} />
          <Route path="/embed/partners/:id" element={<ScorecardEmbed />} />
          {Object.entries(ALIASES).map(([from, to]) => <Route key={from} path={from} element={<Navigate to={to} replace />} />)}
          <Route path="/team" element={<Team />} />
          <Route path="/authors/:slug" element={<Author />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <UrlNormalizer />
      <ScrollToTop />
      <DragScroll />
      <ErrorBoundary>
      <AuthProvider>
      <DestinationsProvider>
      <LocaleProvider>
      <RadioProvider>
      <Layout>
        <AppRoutes />
      </Layout>
      </RadioProvider>
      </LocaleProvider>
      </DestinationsProvider>
      </AuthProvider>
      </ErrorBoundary>
      <Toaster />
      <SonnerToaster position="top-center" closeButton duration={4500} visibleToasts={3} />
    </BrowserRouter>
  );
}

export default App;
